import {
  Bell, Bot, ChevronLeft, CircleDollarSign, Home, LayoutDashboard, ListChecks,
  LogOut, Menu, ReceiptText, Settings, ShieldCheck, Sparkles, Users, UserRound, WalletCards, X
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { Navigate, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useApp } from '../lib/app-context';
import { BrandCover } from './Brand';
import { appearanceFor, appearanceStyles, brandText, useAppearance } from '../lib/appearance';
import { TelegramAvatar } from './TelegramAvatar';

function Logo({ compact = false }: { compact?: boolean }) {
  const { config } = useApp();
  const a=useAppearance();
  return (
    <div className={`wordmark ${compact ? 'compact' : ''}`}>
      <span className="brand-wordmark">{a.logo_url && <img src={a.logo_url} alt="" />}{a.brand_name}</span>
      {!compact && <small>{a.brand_tagline}</small>}
    </div>
  );
}

export function RootShell() {
  const { loading, error, config, refresh } = useApp();
  const location=useLocation();
  if (loading) return <div className="boot"><span>{config?.brand_name || '載入中'}</span><i /></div>;
  if (error) return (
    <div className="fatal-state">
      <ShieldCheck size={30} />
      <h1>無法開啟會員中心</h1>
      <p>{error}</p>
      <button className="button primary" onClick={refresh}>重新整理</button>
    </div>
  );
  if(config?.appearance?.storefront_mode==='showcase'&&/^\/(plans|orders|bank)(\/|$)/.test(location.pathname))return <Navigate to="/" replace/>;
  return <div className="app" style={appearanceStyles(appearanceFor(config))}><Outlet /></div>;
}

const userNav = [
  { to: '/', label: '首頁', icon: Home, end: true },
  { to: '/plans', label: '訂閱', icon: WalletCards },
  { to: '/orders', label: '消費紀錄', icon: ReceiptText },
  { to: '/profile', label: '我的', icon: UserRound }
];

export function UserShell() {
  const { isAdmin, user, demo, config, plans } = useApp();
  const a=useAppearance();
  const navigation=userNav.map((item,index)=>({...item,label:a[['nav_home','nav_plans','nav_orders','nav_profile'][index]]}));
  const location = useLocation();
  if(a.storefront_mode==='showcase')return <div className="showcase-app">
    <header className="member-topbar"><Logo/><div className="topbar-actions">{isAdmin&&<NavLink to="/admin" className="admin-jump"><LayoutDashboard size={16}/>管理後台</NavLink>}<NavLink to="/profile" aria-label="我的帳號"><TelegramAvatar user={user}/></NavLink></div></header>
    <main className="showcase-main" key={location.pathname}><Outlet/></main>
    <nav className="showcase-nav" aria-label="展示導覽"><NavLink to="/" end><Home size={21}/>首頁</NavLink><NavLink to="/profile"><UserRound size={21}/>我的</NavLink></nav>
  </div>;
  return (
    <div className="member-app">
      <aside className="social-sidebar"><Logo /><nav>{navigation.map(({to,label,icon:Icon,end}) => <NavLink key={to} to={to} end={end}><Icon size={25} /><span>{label}</span></NavLink>)}{isAdmin && <NavLink to="/admin"><LayoutDashboard size={25} /><span>管理後台</span></NavLink>}</nav><NavLink className="subscribe-button" to="/plans">{a.view_plans_label}<WalletCards size={20} /></NavLink><div className="sidebar-account"><TelegramAvatar user={user} /><div><strong>{user?.first_name || a.guest_name}</strong><small>{user?.username ? `@${user.username}` : a.guest_subtitle}</small></div></div></aside>
      {demo && <div className="demo-bar">預覽模式 · {isAdmin ? '管理員帳號' : '一般會員帳號'}</div>}
      <header className="member-topbar">
        <Logo />
        <div className="topbar-actions">
          {isAdmin && <NavLink to="/admin" className="admin-jump"><LayoutDashboard size={16} />管理後台</NavLink>}
          <NavLink to="/profile" aria-label="我的帳號"><TelegramAvatar user={user} /></NavLink>
        </div>
      </header>
      <main className="member-main" key={location.pathname}><Outlet /><footer className="mobile-brand-footer">{brandText(a.footer_text,a)}</footer></main>
      <aside className="social-right"><div className="right-label">{a.sidebar_label}</div><article><BrandCover compact /><div className="right-content"><h2>{config?.brand_name}</h2><p>{brandText(a.sidebar_intro,a)}</p><NavLink to="/plans" className="text-link">{plans.length} 種訂閱方案<ChevronLeft size={16} style={{transform:'rotate(180deg)'}} /></NavLink></div></article><section><h3>{a.sidebar_title}</h3><p>{a.sidebar_body}</p><NavLink to="/profile">{a.sidebar_link} →</NavLink></section><p className="right-footer">{brandText(a.footer_text,a)}</p></aside>
      <nav className="bottom-nav" aria-label="會員功能">
        {navigation.map(({ to, label, icon: Icon, end }) => (
          <NavLink to={to} end={end} key={to} className={({ isActive }) => isActive ? 'active' : ''}>
            <Icon size={19} strokeWidth={1.8} />
            <span>{label}</span>
          </NavLink>
        ))}
      </nav>
    </div>
  );
}

export function GuardedAdmin() {
  const { isAdmin } = useApp();
  return isAdmin ? <Outlet /> : <Navigate to="/" replace />;
}

const adminNav = [
  { to: '/admin', label: '總覽', icon: LayoutDashboard, end: true },
  { to: '/admin/members', label: '會員管理', icon: Users },
  { to: '/admin/plans', label: '訂閱方案', icon: CircleDollarSign },
  { to: '/admin/bank', label: '匯款審核', icon: ReceiptText },
  { to: '/admin/automation', label: '歡迎與回覆', icon: Bot },
  { to: '/admin/notifications', label: '通知中心', icon: Bell },
  { to: '/admin/appearance', label: '外觀設定', icon: Settings },
  { to: '/admin/settings', label: '群組設定', icon: Settings },
  { to: '/admin/security', label: '安全中心', icon: ShieldCheck },
  { to: '/admin/audit', label: '操作紀錄', icon: ListChecks }
];

const pageTitles: Record<string, { eyebrow: string; title: string }> = {
  '/admin': { eyebrow: 'Overview', title: '營運總覽' },
  '/admin/members': { eyebrow: 'Members', title: '會員管理' },
  '/admin/plans': { eyebrow: 'Subscriptions', title: '訂閱方案' },
  '/admin/bank': { eyebrow: 'Bank transfers', title: '匯款審核與收款設定' },
  '/admin/automation': { eyebrow: 'Automation', title: '歡迎與自動回覆' },
  '/admin/notifications': { eyebrow: 'Broadcast', title: '通知中心' },
  '/admin/appearance': { eyebrow: 'Appearance', title: '外觀設定' },
  '/admin/settings': { eyebrow: 'Group settings', title: '群組與客服設定' },
  '/admin/security': { eyebrow: 'Security', title: '安全與權限中心' },
  '/admin/audit': { eyebrow: 'Audit log', title: '操作紀錄' }
};

export function AdminShell() {
  const a=useAppearance();
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const info = pageTitles[location.pathname] || pageTitles['/admin'];
  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <div className="admin-app">
      <aside className={`admin-sidebar ${open ? 'open' : ''}`}>
        <div className="sidebar-head"><Logo /><button className="icon-button sidebar-close" aria-label="關閉完整選單" onClick={() => setOpen(false)}><X size={20} /></button></div>
        <nav>
          {adminNav.map(({ to, label, icon: Icon, end }) => (
            <NavLink to={to} end={end} key={to} className={({ isActive }) => isActive ? 'active' : ''}>
              <Icon size={18} strokeWidth={1.8} /><span>{label}</span>
            </NavLink>
          ))}
        </nav>
        <div className="sidebar-foot">
          <button onClick={() => navigate('/')}><ChevronLeft size={17} />回會員中心</button>
          <p><Sparkles size={13} />會員系統運作中</p>
        </div>
      </aside>
      {open && <button className="sidebar-scrim" aria-label="關閉選單" onClick={() => setOpen(false)} />}
      <div className="admin-workspace">
        <header className="admin-topbar">
          <button className="icon-button mobile-menu" aria-label="開啟完整選單" aria-expanded={open} onClick={() => setOpen(true)}><Menu size={21} /></button>
          <div><p>{info.eyebrow}</p><h1>{info.title}</h1></div>
          <button className="admin-avatar" onClick={() => navigate('/profile')} title="回到個人頁面">管</button>
        </header>
        <main className="admin-main">{a.storefront_mode==='showcase'&&<div className="bank-notice"><strong>展示模式已啟用</strong><p>前台訂閱與付款已隱藏，到期提醒及訂閱自動化暫停。<NavLink to="/admin/appearance">編輯展示與網址按鈕</NavLink></p></div>}<Outlet /></main>
      </div>
      <nav className="admin-mobile-nav" aria-label="後台快捷導覽">
        {adminNav.filter(({to}) => ['/admin','/admin/members','/admin/plans','/admin/notifications'].includes(to)).map(({ to, label, icon: Icon, end }) => (
          <NavLink to={to} end={end} key={to} className={({ isActive }) => isActive ? 'active' : ''}>
            <Icon size={18} /><span>{label.replace('管理', '').replace('中心', '')}</span>
          </NavLink>
        ))}
        <button className={open || !['/admin','/admin/members','/admin/plans','/admin/notifications'].includes(location.pathname) ? 'active' : ''} aria-expanded={open} onClick={() => setOpen(!open)}><Menu size={18}/><span>更多</span></button>
      </nav>
    </div>
  );
}

export function PageLoading({ label = '資料載入中' }: { label?: string }) {
  return <div className="page-loading"><i /><p>{label}</p></div>;
}

export function EmptyState({ icon: Icon = ReceiptText, title, body }: { icon?: any; title: string; body: string }) {
  return <div className="empty-state"><Icon size={27} /><h3>{title}</h3><p>{body}</p></div>;
}
